import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import {
    Compass, ArrowRight, Shield, Stethoscope, HeartHandshake, Home, Heart,
    Sun, Users, BookOpen, FileText, ScrollText, Sparkles, Brain, Building2,
    Pill, AlertOctagon, Calendar, Lock, ScanLine, MessageSquare, Network,
} from "lucide-react";
import NdaDownloadGate from "@/components/NdaDownloadGate";

/* /partners — enterprise partnership page.
 *
 * Positioning: Memory Lane is the continuity platform for dementia care.
 * Not another dementia app. Not a competitor to neurology, GUIDE, home
 * health, or hospice. The continuity layer that strengthens every link
 * in the existing dementia care ecosystem.
 *
 * Page goal: answer "Why would our organization partner with Memory Lane?"
 * in the first 30 seconds, then prove it across the sections below.
 *
 * The page repeats one signature line in three places (hero band, ecosystem
 * graphic, Trust Center) — that's intentional. Brand repetition = recall.
 */
const SIGNATURE = "Memory Lane is the trusted front door for memory care — supporting families from the first signs of memory change through every stage of care.";


const FRAGMENTED_STEPS = [
    "Diagnosis", "Neurology", "GUIDE Program", "Home Health",
    "Hospice", "Community Resources", "Legal Planning", "Caregiver Wellness",
];

// Memory Lane is FIRST — the front door before diagnosis — then stays
// alongside families through every team and program that follows.
const CONTINUITY_STEPS = [
    "Memory Lane", "Primary Care", "Neurology", "GUIDE", "Home Health",
    "Hospice", "Community Resources", "Wellness", "Legal Planning", "Future Services",
];

const COMPLEMENTS = [
    {
        org: "Neurology",
        them: "You diagnose and manage the disease.",
        us: "Memory Lane prepares caregivers between appointments and helps them understand what comes next.",
        Icon: Brain,
        tone: "bg-teal-soft text-teal-dark border-teal-light",
    },
    {
        org: "GUIDE Programs",
        them: "You provide valuable care navigation and support.",
        us: "Memory Lane helps caregivers prepare before enrollment, complete eligibility screeners, coordinate referrals, stay organized throughout participation, and continue receiving support as needs evolve.",
        Icon: Stethoscope,
        tone: "bg-stage_early/30 text-stage_early-dark border-stage_early-soft",
    },
    {
        org: "Home Health",
        them: "You deliver in-home clinical care.",
        us: "Memory Lane prepares families before services begin and keeps everyone informed during transitions.",
        Icon: Home,
        tone: "bg-sage-light text-sage-dark border-sage-soft",
    },
    {
        org: "Hospice",
        them: "You provide end-of-life care and comfort.",
        us: "Memory Lane supports caregivers before hospice, throughout hospice, and afterward through education and caregiver support.",
        Icon: Heart,
        tone: "bg-coral-light text-coral-dark border-coral-softer",
    },
    {
        org: "Community Organizations",
        them: "You provide local services and support.",
        us: "Memory Lane helps caregivers discover and connect with trusted local resources at the right time.",
        Icon: Users,
        tone: "bg-yellow-light text-yellow-dark border-yellow",
    },
];

const PARTNER_BENEFITS = [
    "Better prepared caregivers",
    "More productive appointments",
    "Reduced caregiver overwhelm",
    "One evolving caregiver story",
    "Fewer repeated intake conversations",
    "Simplified referrals",
    "Improved care coordination",
    "Support available between scheduled visits",
    "Higher caregiver engagement",
    "Better continuity from first concerns through every stage of care",
];

const CONTINUOUS_SUPPORT = [
    { label: "Personalized roadmap", Icon: Compass },
    { label: "AI that remembers their story", Icon: Sparkles },
    { label: "Appointment preparation", Icon: Calendar },
    { label: "Behavior education", Icon: Brain },
    { label: "Treatment education", Icon: Pill },
    { label: "Clinical trial education", Icon: ScanLine },
    { label: "Resource recommendations", Icon: BookOpen },
    { label: "Care Team Summary", Icon: FileText },
    { label: "Caregiver wellness support", Icon: Heart },
    { label: "Optional human care navigation sessions", Icon: MessageSquare },
];

const GUIDE_SUPPORTS = [
    "Guide families through eligibility screeners",
    "Help organize required documentation",
    "Coordinate referrals to participating GUIDE organizations",
    "Assist with NP attestations when applicable",
    "Continue supporting caregivers throughout and beyond GUIDE participation",
];

const ECOSYSTEM = [
    "Neurology", "GUIDE", "Home Health", "Hospice", "Primary Care",
    "Community Resources", "Caregiver Wellness", "Clinical Trials",
    "Legal & Financial Planning", "Memory Care Communities",
    "Transportation", "Adult Day Programs", "Support Groups",
];


export default function Partners() {
    return (
        <div className="min-h-screen bg-cream" data-testid="partners-page">
            <SeoHead
                title="For Healthcare Partners — Continuity for Dementia Care | Memory Lane"
                description="Memory Lane is the continuity platform for dementia care — partner with us if you run a neurology clinic, GUIDE participating provider, home health agency, hospice, or senior-living group. Reduce caregiver burnout, improve patient outcomes."
                canonicalPath="/partners"
            />
            {/* Top bar */}
            <header
                className="bg-cream/80 backdrop-blur-sm sticky top-0 z-30 border-b border-[#E2E8F0]/60"
                data-testid="partners-topbar"
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2.5">
                        <span className="w-9 h-9 rounded-2xl bg-teal text-white flex items-center justify-center">
                            <Compass strokeWidth={1.8} size={18} />
                        </span>
                        <div className="leading-tight">
                            <div className="font-heading font-semibold text-[#1F2624]">Memory Lane</div>
                            <div className="text-xs text-[#4C5551] -mt-0.5">For Healthcare Partners</div>
                        </div>
                    </Link>
                    <Link
                        to="/"
                        data-testid="partners-back-home"
                        className="text-sm font-medium text-teal-dark hover:underline"
                    >
                        ← Home
                    </Link>
                </div>
            </header>

            {/* ========== Hero ========== */}
            <section
                className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-12 md:pb-16"
                data-testid="partners-hero"
            >
                <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-3 flex items-center gap-2">
                    <Network size={16} strokeWidth={1.8} /> For healthcare partners
                </p>
                <h1
                    className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tight leading-[1.05] text-[#12776F]"
                    data-testid="partners-hero-headline"
                >
                    The Continuity Platform for <span className="text-teal-dark">Dementia Care.</span>
                </h1>
                <p className="mt-5 text-lg md:text-xl text-[#4C5551] leading-relaxed max-w-3xl">
                    Memory Lane provides one continuous home for families — from the first signs of memory change, through evaluation and diagnosis, into dementia care, and on to advanced stages —
                    connecting families with providers, programs, and resources while preserving continuity
                    across every stage of care.
                </p>
                <p className="mt-3 text-lg md:text-xl text-[#1F2624] leading-relaxed max-w-3xl">
                    We don&apos;t replace providers. <strong className="text-teal-dark">We strengthen the connections between them.</strong>
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                        to="/partners/inquiry"
                        data-testid="partners-cta-partner"
                        className="ml-btn-primary !min-h-[48px] !px-6 inline-flex items-center gap-2"
                    >
                        <HeartHandshake size={16} strokeWidth={1.8} /> Partner with us
                    </Link>
                    <Link
                        to="/partners/inquiry?intent=demo"
                        data-testid="partners-cta-demo"
                        className="!min-h-[48px] !px-6 inline-flex items-center gap-2 rounded-full font-medium bg-white border-2 border-teal-dark text-teal-dark hover:bg-teal-soft/40 transition-colors"
                    >
                        <Calendar size={16} strokeWidth={1.8} /> Schedule a demo
                    </Link>
                </div>
            </section>

            {/* ========== Signature band ========== */}
            <section
                className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2 mb-12"
                data-testid="partners-signature-band"
            >
                <div className="ml-card p-6 md:p-7 bg-teal-soft/30 border border-teal-light text-center">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2">Our promise</p>
                    <p className="font-heading text-xl md:text-2xl text-[#12776F] leading-snug max-w-3xl mx-auto">
                        Memory Lane is{" "}
                        <span className="text-teal-dark font-semibold">the trusted front door for memory care</span>{" "}
                        — supporting families from the first signs of memory change through every stage of care.
                    </p>
                </div>
            </section>

            {/* ========== Who buys Memory Lane ========== */}
            <section
                className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 md:pb-16"
                data-testid="partners-who-buys"
            >
                <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">Who we work with</p>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                    Who buys Memory Lane.
                </h2>
                <p className="mt-3 text-lg text-[#4C5551] max-w-3xl">
                    The caregiver is the user. The organization is the customer. The platform creates value for both.
                </p>
                <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="partners-who-buys-grid">
                    {[
                        { title: "GUIDE participating organizations", body: "Meet caregiver-support and care-navigation expectations with structured caregiver data, burden tracking, and between-visit engagement your navigators can act on.", Icon: Stethoscope, tone: "bg-stage_early/30 text-stage_early-dark" },
                        { title: "Neurology & memory care practices", body: "Walk into every visit with interval history, behavior timelines, and caregiver-reported changes already organized — instead of reconstructing months in the first ten minutes.", Icon: Brain, tone: "bg-teal-soft text-teal-dark" },
                        { title: "Health systems & ACOs", body: "A dementia care navigation layer that spans service lines and transitions — without building or maintaining it inside the EHR.", Icon: Building2, tone: "bg-ml_blue-light text-ml_blue-dark" },
                        { title: "Home health & hospice agencies", body: "Smoother handoffs: the family's story, medications, and behavior history arrive with them — not in a fax that never comes.", Icon: Home, tone: "bg-coral-light text-coral-dark" },
                    ].map(({ title, body, Icon, tone }) => (
                        <div key={title} className="ml-card p-5 md:p-6 bg-white" data-testid={`who-buys-${title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                            <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${tone}`}>
                                <Icon size={18} strokeWidth={1.7} />
                            </span>
                            <h3 className="mt-3 font-heading text-lg font-semibold text-[#1F2624]">{title}</h3>
                            <p className="mt-1.5 text-sm text-[#4C5551] leading-relaxed">{body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ========== Built for the whole care team ========== */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 md:pb-16" data-testid="partners-care-team-roles">
                <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">One platform, every role</p>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                    Built for the whole dementia care team.
                </h2>
                <p className="mt-3 text-lg text-[#4C5551] max-w-3xl">
                    Care navigators are one important user — not the only one. Every role gains better caregiver
                    context and longitudinal information between visits.
                </p>
                <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="partners-roles-grid">
                    {[
                        { r: "Care Navigators", Icon: Compass, d: "Arrive prepared; coach instead of collect." },
                        { r: "Primary Care", Icon: Stethoscope, d: "Interval history before the 15-minute visit." },
                        { r: "Neurologists", Icon: Brain, d: "Behavior and medication changes, organized." },
                        { r: "Geriatricians", Icon: Heart, d: "Function, falls, and caregiver strain trends." },
                        { r: "Home Health", Icon: Home, d: "The home story arrives with the referral." },
                        { r: "Hospice", Icon: Sun, d: "Smoother transitions with full context." },
                        { r: "Palliative Care", Icon: HeartHandshake, d: "Goals and priorities, already captured." },
                        { r: "Social Workers", Icon: Users, d: "Family dynamics and needs between visits." },
                        { r: "Case Managers", Icon: FileText, d: "Longitudinal record across settings." },
                        { r: "Population Health", Icon: Network, d: "Caregiver-reported signal at scale." },
                        { r: "GUIDE Programs", Icon: ScrollText, d: "Assessments and documentation support." },
                    ].map(({ r, Icon, d }) => (
                        <div key={r} className="ml-card p-3.5 bg-white" data-testid={`role-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            <Icon size={17} strokeWidth={1.7} className="text-teal-dark" />
                            <div className="mt-1.5 text-sm font-heading font-semibold text-[#1F2624]">{r}</div>
                            <div className="text-xs text-[#6E7A74] leading-snug mt-0.5">{d}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ========== Fragmented today ========== */}
            <section
                className="bg-cream-light border-y border-cream-dark"
                data-testid="partners-fragmented"
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
                    <p className="text-sm uppercase tracking-wider font-semibold text-coral-dark mb-2">The problem</p>
                    <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                        Dementia care is fragmented.
                    </h2>

                    <div className="mt-8 grid md:grid-cols-2 gap-8 items-start">
                        <ol
                            className="ml-card p-6 bg-white space-y-2 text-base text-[#1F2624]"
                            data-testid="partners-fragmented-list"
                        >
                            {FRAGMENTED_STEPS.map((step, i) => (
                                <li key={step} className="flex items-center gap-3">
                                    <span className="w-7 h-7 rounded-full bg-cream-dark/50 text-[#4C5551] text-xs flex items-center justify-center font-mono shrink-0">
                                        {i + 1}
                                    </span>
                                    <span>{step}</span>
                                    {i < FRAGMENTED_STEPS.length - 1 && (
                                        <span className="text-[#6E7A74] ml-auto text-sm">↓</span>
                                    )}
                                </li>
                            ))}
                        </ol>
                        <div className="space-y-4">
                            <p className="text-base text-[#4C5551] leading-relaxed">Every transition often means:</p>
                            <ul className="space-y-2 text-base text-[#1F2624]">
                                {[
                                    "Another intake",
                                    "Another history",
                                    "Another questionnaire",
                                    "Another provider",
                                    "Another place to remember everything",
                                ].map((line) => (
                                    <li key={line} className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-coral-dark" />
                                        {line}
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-4 text-base font-medium text-[#1F2624]">
                                This creates caregiver overwhelm and fragmented communication.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== Memory Lane is the continuity layer ========== */}
            <section
                className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20"
                data-testid="partners-continuity-layer"
            >
                <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">Our role</p>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                    The front door — and the continuity layer.
                </h2>
                <p className="mt-3 text-lg text-[#4C5551] max-w-3xl">
                    Families don&apos;t need a diagnosis to start with Memory Lane. We help them get one, then stay with them through every team and program that follows.
                </p>

                <div className="mt-8 grid md:grid-cols-2 gap-8 items-start">
                    <ol
                        className="ml-card p-6 bg-gradient-to-br from-teal-soft/50 via-cream-light to-stage_early/30 !border-teal-light space-y-2 text-base text-[#1F2624]"
                        data-testid="partners-continuity-list"
                    >
                        {CONTINUITY_STEPS.map((step, i) => {
                            const isUs = step === "Memory Lane";
                            return (
                                <li
                                    key={step}
                                    className={`flex items-center gap-3 ${isUs ? "rounded-2xl bg-teal-dark text-white px-3 py-2 -mx-1 my-1 shadow-sm" : ""}`}
                                >
                                    <span className={`w-7 h-7 rounded-full text-xs flex items-center justify-center font-mono shrink-0 ${isUs ? "bg-white/20 text-white" : "bg-cream-dark/50 text-[#4C5551]"}`}>
                                        {i + 1}
                                    </span>
                                    <span className={isUs ? "font-heading font-medium" : ""}>{step}</span>
                                    {i < CONTINUITY_STEPS.length - 1 && (
                                        <span className={`ml-auto text-sm ${isUs ? "text-white/70" : "text-[#6E7A74]"}`}>↓</span>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                    <div className="space-y-3">
                        <p className="text-base text-[#1F2624] leading-relaxed">
                            Memory Lane stays with the caregiver through every transition.
                        </p>
                        <ul className="space-y-2 text-base text-[#1F2624]">
                            {["One caregiver story.", "One personalized roadmap.", "One continuous journey.", "One place to return."].map(line => (
                                <li key={line} className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-teal-dark" />
                                    {line}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ========== We don't replace existing programs ========== */}
            <section
                className="bg-cream-light border-y border-cream-dark"
                data-testid="partners-complements"
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
                    <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">How we work together</p>
                    <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                        We don&apos;t replace existing programs.
                    </h2>
                    <p className="mt-3 text-lg text-[#4C5551] max-w-3xl">
                        Instead of competing with organizations, Memory Lane complements them.
                    </p>

                    <ul className="mt-8 space-y-4" data-testid="partners-complements-list">
                        {COMPLEMENTS.map(({ org, them, us, Icon, tone }) => (
                            <li
                                key={org}
                                className="ml-card p-5 md:p-6 bg-white"
                                data-testid={`partners-complement-${org.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                            >
                                <div className="flex items-start gap-4">
                                    <span className={`w-12 h-12 rounded-2xl border flex items-center justify-center shrink-0 ${tone}`}>
                                        <Icon size={20} strokeWidth={1.7} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-heading text-xl font-medium text-[#1F2624]">{org}</h3>
                                        <p className="text-sm text-[#4C5551] mt-1 leading-relaxed">{them}</p>
                                        <p className="text-sm text-[#1F2624] mt-2 leading-relaxed">
                                            <span className="text-teal-dark font-medium">Memory Lane</span> {us.replace(/^Memory Lane\s*/i, "")}
                                        </p>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ========== From caregiver to care team (structured data pipeline) ========== */}
            <section
                className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20"
                data-testid="partners-data-pipeline"
            >
                <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">How the data flows</p>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                    How caregivers generate structured information.
                </h2>
                <p className="mt-3 text-lg text-[#4C5551] max-w-3xl">
                    Families capture life at home in plain language. Memory Lane turns it into clinical-grade signal your team can use — and the caregiver controls exactly what is shared.
                </p>
                <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid="partners-pipeline-steps">
                    {[
                        { step: "1", title: "Caregivers capture life at home", body: "Behaviors, sleep, falls, medications, wellness check-ins, and questions — logged in the moment, in their own words, with Lane's help at 2 AM.", Icon: Heart, tone: "bg-teal-soft text-teal-dark" },
                        { step: "2", title: "Memory Lane structures it", body: "Longitudinal timelines, behavior patterns, caregiver burden trends, FAST/Zarit context, and AI-generated visit summaries — organized automatically.", Icon: Sparkles, tone: "bg-ml_blue-light text-ml_blue-dark" },
                        { step: "3", title: "Your team receives it", body: "A permission-based dashboard with interval history, risk signals, and visit prep — only what the caregiver has explicitly chosen to share.", Icon: Stethoscope, tone: "bg-stage_early/30 text-stage_early-dark" },
                    ].map(({ step, title, body, Icon, tone }) => (
                        <div key={step} className="ml-card p-5 md:p-6 bg-white relative" data-testid={`pipeline-step-${step}`}>
                            <span className="absolute top-4 right-5 font-heading font-bold text-3xl text-cream-dark">{step}</span>
                            <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${tone}`}>
                                <Icon size={18} strokeWidth={1.7} />
                            </span>
                            <h3 className="mt-3 font-heading text-lg font-semibold text-[#1F2624]">{title}</h3>
                            <p className="mt-1.5 text-sm text-[#4C5551] leading-relaxed">{body}</p>
                        </div>
                    ))}
                </div>
                <p className="mt-6 text-sm text-[#4C5551] flex items-center gap-2" data-testid="partners-pipeline-ownership">
                    <Lock size={14} strokeWidth={1.8} className="text-teal-dark shrink-0" />
                    Caregivers own their record. Organizations see only what a family turns on — per data type, per document, pausable any time.
                </p>
            </section>

            {/* ========== Not another EHR ========== */}
            <section
                className="bg-ml_blue-light/40 border-y border-ml_blue-soft/60"
                data-testid="partners-not-ehr"
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
                    <p className="text-sm uppercase tracking-wider font-semibold text-ml_blue-dark mb-2">Where we sit</p>
                    <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                        Memory Lane complements the EHR — it doesn&apos;t replace it.
                    </h2>
                    <div className="mt-8 grid md:grid-cols-2 gap-6 items-start">
                        <div className="ml-card p-6 bg-white">
                            <h3 className="font-heading text-lg font-semibold text-[#1F2624]">Your EHR owns</h3>
                            <ul className="mt-3 space-y-2 text-base text-[#1F2624]">
                                {["The clinical record of each episode", "Orders, results, billing, documentation", "What happens inside your walls"].map((l) => (
                                    <li key={l} className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#9CA6A1] mt-2.5 shrink-0" />{l}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="ml-card p-6 bg-white !border-teal-light">
                            <h3 className="font-heading text-lg font-semibold text-teal-dark">Memory Lane owns</h3>
                            <ul className="mt-3 space-y-2 text-base text-[#1F2624]">
                                {["The between-visit journey at home", "The caregiver's longitudinal story across every provider", "Structured caregiver-reported data your EHR never captures"].map((l) => (
                                    <li key={l} className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-teal-dark mt-2.5 shrink-0" />{l}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <p className="mt-6 text-base text-[#1F2624] max-w-3xl leading-relaxed" data-testid="partners-not-ehr-note">
                        Summaries export as print-ready documents that drop into your existing workflow today, and the
                        platform is structured for standards-based integration (SMART on FHIR) as programs mature.
                    </p>
                </div>
            </section>

            {/* ========== The Care Team Summary (flagship) ========== */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid="partners-care-team-summary">
                <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">Flagship feature</p>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                    The Care Team Summary.
                </h2>
                <p className="mt-3 text-lg text-[#4C5551] max-w-3xl">
                    A communication tool — not just documentation. It translates weeks or months of caregiving
                    into a concise clinical summary, so every clinician immediately understands what happened
                    between encounters.
                </p>
                <div className="mt-7 ml-card p-6 bg-white">
                    <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-3">Every summary surfaces</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2" data-testid="partners-summary-items">
                        {["Recent caregiver concerns", "Behavior changes", "Medication changes",
                          "Interventions attempted", "What has helped", "What has not helped",
                          "Caregiver priorities", "Questions for the care team", "Caregiver wellness trends",
                          "Standardized assessments (FAST, ZBI)", "Longitudinal change since last visit"].map((i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-[#1F2624]">
                                <span className="w-1.5 h-1.5 rounded-full bg-teal-dark mt-2 shrink-0" />{i}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ========== Navigator + clinical workflows ========== */}
            <section className="bg-white/70 border-y border-cream-dark" data-testid="partners-workflows">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
                    <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                        Two workflows, one continuous record.
                    </h2>
                    <div className="mt-8 grid md:grid-cols-2 gap-5 items-stretch">
                        <div className="ml-card p-6 bg-white flex flex-col" data-testid="partners-navigator-workflow">
                            <span className="w-11 h-11 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center"><Compass size={18} strokeWidth={1.7} /></span>
                            <h3 className="mt-3 font-heading text-xl font-semibold text-[#1F2624]">The care navigator's operating platform</h3>
                            <p className="mt-1.5 text-sm text-[#4C5551]">Between visits, Memory Lane continuously collects behavior tracking, caregiver concerns, wellness, standardized assessments, questions, and intervention history.</p>
                            <p className="mt-3 text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">Before outreach, navigators see</p>
                            <ul className="mt-1.5 space-y-1.5 text-sm text-[#1F2624]">
                                {["FAST progression", "Zarit Burden trends", "Caregiver priorities & outstanding concerns", "Behavior changes & intervention effectiveness", "Suggested care plan updates"].map((i) => (
                                    <li key={i} className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-teal-dark mt-2 shrink-0" />{i}</li>
                                ))}
                            </ul>
                            <p className="mt-4 text-sm font-medium text-teal-dark">Navigators spend their time coaching families and building care plans — not collecting information and administering assessments.</p>
                        </div>
                        <div className="ml-card p-6 bg-white flex flex-col" data-testid="partners-clinical-workflow">
                            <span className="w-11 h-11 rounded-2xl bg-ml_blue-light text-ml_blue-dark flex items-center justify-center"><Stethoscope size={18} strokeWidth={1.7} /></span>
                            <h3 className="mt-3 font-heading text-xl font-semibold text-[#1F2624]">The clinician's head start</h3>
                            <p className="mt-1.5 text-sm text-[#4C5551]">Instead of opening with <em>“What has happened since I last saw you?”</em>, clinicians receive a concise longitudinal summary of the caregiver's experience between visits.</p>
                            <p className="mt-3 text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">So visits focus on</p>
                            <ul className="mt-1.5 space-y-1.5 text-sm text-[#1F2624]">
                                {["Clinical decision making", "Medication adjustments", "Safety concerns", "Behavior management", "Care planning & education", "Shared decision making"].map((i) => (
                                    <li key={i} className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-ml_blue-dark mt-2 shrink-0" />{i}</li>
                                ))}
                            </ul>
                            <p className="mt-4 text-sm font-medium text-ml_blue-dark">Less appointment time gathering basic history. More time providing care.</p>
                        </div>
                    </div>

                    {/* Administrative efficiency */}
                    <div className="mt-5 ml-card p-6 bg-white" data-testid="partners-admin-efficiency">
                        <div className="flex items-center gap-3">
                            <span className="w-11 h-11 rounded-2xl bg-yellow-light text-yellow-dark flex items-center justify-center shrink-0"><FileText size={18} strokeWidth={1.7} /></span>
                            <h3 className="font-heading text-xl font-semibold text-[#1F2624]">Less administrative burden — without touching your EHR</h3>
                        </div>
                        <p className="mt-2 text-sm text-[#4C5551] max-w-3xl">Information collected between visits is continuously organized into documentation-ready summaries that support:</p>
                        <div className="mt-3 flex flex-wrap gap-2" data-testid="partners-admin-workflows">
                            {["Navigator documentation", "GUIDE documentation", "Care plans", "Follow-up notes", "Caregiver assessments", "Longitudinal tracking", "Quality reporting"].map((w) => (
                                <span key={w} className="px-3 py-1.5 rounded-2xl bg-cream-dark/60 text-xs font-semibold text-[#4C5551]">{w}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== Why organizations partner with Memory Lane ========== */}
            <section
                className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20"
                data-testid="partners-benefits"
            >
                <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">The case for partnership</p>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                    Why organizations partner with Memory Lane.
                </h2>

                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="partners-benefits-list">
                    {PARTNER_BENEFITS.map((b, i) => (
                        <li
                            key={b}
                            data-testid={`partner-benefit-${i + 1}`}
                            className="ml-card p-4 bg-white flex items-center gap-3"
                        >
                            <span className="w-9 h-9 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center shrink-0">
                                <Sparkles size={14} strokeWidth={1.8} />
                            </span>
                            <span className="text-sm text-[#1F2624] font-medium">{b}</span>
                        </li>
                    ))}
                </ul>
            </section>

            {/* ========== Continuous support between visits ========== */}
            <section
                className="bg-gradient-to-br from-teal-soft/30 via-cream-light to-stage_early/20 border-y border-teal-light/60"
                data-testid="partners-continuous-support"
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
                    <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">Between scheduled visits</p>
                    <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                        Continuous support between visits.
                    </h2>
                    <p className="mt-3 text-lg text-[#4C5551] max-w-3xl">
                        Caregivers don&apos;t stop needing help when the appointment ends. Memory Lane provides:
                    </p>

                    <ul className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="partners-continuous-list">
                        {CONTINUOUS_SUPPORT.map(({ label, Icon }) => (
                            <li key={label} className="ml-card p-4 bg-white flex flex-col items-start gap-2">
                                <span className="w-9 h-9 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center shrink-0">
                                    <Icon size={16} strokeWidth={1.8} />
                                </span>
                                <span className="text-sm text-[#1F2624] font-medium leading-snug">{label}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-7 text-base md:text-lg text-[#1F2624] max-w-3xl leading-relaxed">
                        Caregivers can receive guidance whenever questions arise — while keeping
                        important information organized for future appointments.
                    </p>
                </div>
            </section>

            {/* ========== Helping families navigate GUIDE ========== */}
            <section
                className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20"
                data-testid="partners-guide-section"
            >
                <div className="flex items-start gap-3 mb-2">
                    <span className="w-10 h-10 rounded-2xl bg-stage_early text-white flex items-center justify-center shrink-0">
                        <Stethoscope size={18} strokeWidth={1.7} />
                    </span>
                    <div>
                        <p className="text-sm uppercase tracking-wider font-semibold text-stage_early-dark">Medicare GUIDE Model</p>
                        <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                            Helping families navigate GUIDE.
                        </h2>
                    </div>
                </div>

                <div className="mt-6 ml-card p-6 md:p-7 bg-stage_early/20 border border-stage_early-soft">
                    <p className="text-base text-[#1F2624] leading-relaxed">
                        Memory Lane helps caregivers determine whether they may qualify for the GUIDE Model.
                        We can:
                    </p>
                    <ul className="mt-4 space-y-2 text-base text-[#1F2624]" data-testid="partners-guide-list">
                        {GUIDE_SUPPORTS.map((s) => (
                            <li key={s} className="flex items-start gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-stage_early-dark mt-2.5 shrink-0" />
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-5 text-base font-medium text-stage_early-dark">
                        Memory Lane complements GUIDE — it does not replace it.
                    </p>
                </div>
            </section>

            {/* ========== The dementia ecosystem graphic ========== */}
            <section
                className="bg-cream-light border-y border-cream-dark"
                data-testid="partners-ecosystem"
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-center">
                    <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">The big picture</p>
                    <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                        The dementia ecosystem.
                    </h2>
                    <p className="mt-3 text-base md:text-lg text-[#4C5551] max-w-2xl mx-auto">
                        Memory Lane connects the caregiver to every organization — rather than replacing them.
                    </p>

                    <div className="mt-10 relative" data-testid="partners-ecosystem-graphic">
                        {/* center node */}
                        <div className="relative max-w-xs mx-auto">
                            <div className="ml-card p-6 md:p-8 bg-gradient-to-br from-teal-dark to-teal text-white shadow-xl">
                                <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">Memory Lane</p>
                                <p className="font-heading text-2xl md:text-3xl mt-1 leading-tight">
                                    The Continuity Platform
                                </p>
                                <p className="text-xs opacity-90 mt-2">
                                    {SIGNATURE}
                                </p>
                            </div>
                        </div>

                        {/* surrounding organizations */}
                        <ul
                            className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
                            data-testid="partners-ecosystem-orgs"
                        >
                            {ECOSYSTEM.map((org) => (
                                <li
                                    key={org}
                                    className="rounded-2xl bg-white border border-cream-dark px-4 py-3 text-sm font-medium text-[#1F2624] text-center hover:border-teal hover:bg-teal-soft/30 transition-colors"
                                >
                                    {org}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ========== Healthcare Trust Center (renamed from security/legal block) ========== */}
            <section
                className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20"
                data-testid="partners-trust-center"
            >
                <div className="ml-card p-6 md:p-10 bg-white">
                    <div className="flex items-start gap-3 mb-2">
                        <span className="w-10 h-10 rounded-2xl bg-sage text-white flex items-center justify-center shrink-0">
                            <Shield size={18} strokeWidth={1.7} />
                        </span>
                        <div>
                            <p className="text-sm uppercase tracking-wider font-semibold text-sage-dark">Partner with confidence</p>
                            <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                                Healthcare Trust Center.
                            </h2>
                            <p className="mt-2 text-base md:text-lg text-[#4C5551] max-w-2xl">
                                Everything your organization needs to confidently partner with Memory Lane.
                            </p>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <Link
                            to="/security"
                            data-testid="trust-link-security"
                            className="ml-card ml-card-hover p-4 bg-cream-light border-cream-dark flex flex-col gap-2"
                        >
                            <span className="w-9 h-9 rounded-2xl bg-sage-light text-sage-dark flex items-center justify-center">
                                <Lock size={16} strokeWidth={1.8} />
                            </span>
                            <p className="font-heading font-medium text-[#1F2624]">Security &amp; Privacy</p>
                            <p className="text-xs text-[#4C5551]">Our practices, infrastructure, data handling.</p>
                        </Link>
                        <Link
                            to="/privacy"
                            data-testid="trust-link-privacy"
                            className="ml-card ml-card-hover p-4 bg-cream-light border-cream-dark flex flex-col gap-2"
                        >
                            <span className="w-9 h-9 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center">
                                <ScrollText size={16} strokeWidth={1.8} />
                            </span>
                            <p className="font-heading font-medium text-[#1F2624]">Privacy Policy</p>
                            <p className="text-xs text-[#4C5551]">How we handle caregiver and partner data.</p>
                        </Link>
                        <Link
                            to="/terms"
                            data-testid="trust-link-terms"
                            className="ml-card ml-card-hover p-4 bg-cream-light border-cream-dark flex flex-col gap-2"
                        >
                            <span className="w-9 h-9 rounded-2xl bg-yellow-light text-yellow-dark flex items-center justify-center">
                                <FileText size={16} strokeWidth={1.8} />
                            </span>
                            <p className="font-heading font-medium text-[#1F2624]">Terms of Service</p>
                            <p className="text-xs text-[#4C5551]">Acceptable-use, liability, account terms.</p>
                        </Link>
                        <a
                            href="mailto:ashlee@asvmedicalservices.com?subject=Memory%20Lane%20-%20Partnership%20information"
                            data-testid="trust-link-partnership"
                            className="ml-card ml-card-hover p-4 bg-cream-light border-cream-dark flex flex-col gap-2"
                        >
                            <span className="w-9 h-9 rounded-2xl bg-coral-light text-coral-dark flex items-center justify-center">
                                <HeartHandshake size={16} strokeWidth={1.8} />
                            </span>
                            <p className="font-heading font-medium text-[#1F2624]">Partnership information</p>
                            <p className="text-xs text-[#4C5551]">Talk to our team about a pilot.</p>
                        </a>
                    </div>

                    <NdaDownloadGate />
                </div>
            </section>

            {/* ========== Positioning statement ========== */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16" data-testid="partners-positioning">
                <div className="ml-card p-7 md:p-9 bg-teal-dark text-white !border-teal-dark">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-teal-light">What Memory Lane is</p>
                    <p className="mt-3 font-heading text-xl md:text-2xl font-semibold leading-relaxed" data-testid="partners-positioning-statement">
                        Not simply software for caregivers — a longitudinal dementia care platform that supports
                        families while giving the entire care team meaningful clinical context between visits.
                    </p>
                    <div className="mt-5 grid sm:grid-cols-3 gap-4 text-sm text-white/85">
                        <p><b className="text-white">Care navigators</b> get better preparation and automated assessments.</p>
                        <p><b className="text-white">Physicians</b> get concise, structured longitudinal summaries.</p>
                        <p><b className="text-white">Organizations</b> get continuity, efficient documentation, engaged caregivers, and stronger coordination.</p>
                    </div>
                    <p className="mt-5 text-sm text-white/75" data-testid="partners-staffing-note">
                        Need additional care navigation capacity? Memory Lane can also provide experienced dementia
                        care navigators as an optional service.
                    </p>
                </div>
            </section>

            {/* ========== Final closing CTA ========== */}
            <section
                className="bg-gradient-to-br from-teal-dark via-teal to-teal-dark text-white"
                data-testid="partners-final-cta"
            >
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
                    <p className="text-sm uppercase tracking-wider font-semibold opacity-90 mb-3">Memory Lane</p>
                    <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight leading-tight">
                        {SIGNATURE}
                    </h2>
                    <p className="mt-5 text-base md:text-lg opacity-90 max-w-2xl mx-auto leading-relaxed">
                        Built by a Nurse Practitioner with experience across hospice, dementia care, and the CMS GUIDE Model.
                        Designed with caregivers. Built to strengthen the entire dementia care ecosystem.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/partners/inquiry"
                            data-testid="partners-final-cta-partner"
                            className="!min-h-[48px] !px-6 inline-flex items-center gap-2 rounded-full font-medium bg-white text-teal-dark hover:bg-cream-light transition-colors"
                        >
                            <HeartHandshake size={16} strokeWidth={1.8} /> Partner with us
                        </Link>
                        <Link
                            to="/partners/inquiry?intent=demo"
                            data-testid="partners-final-cta-demo"
                            className="!min-h-[48px] !px-6 inline-flex items-center gap-2 rounded-full font-medium bg-white/10 backdrop-blur border-2 border-white/40 text-white hover:bg-white/20 transition-colors"
                        >
                            <Calendar size={16} strokeWidth={1.8} /> Schedule a demo
                        </Link>
                    </div>
                </div>
            </section>

            <footer
                className="py-8 border-t border-[#E2E8F0] bg-cream"
                data-testid="partners-footer"
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-wrap justify-between gap-3 text-sm text-[#4C5551]">
                    <p>© Memory Lane Care Navigator</p>
                    <div className="flex flex-wrap gap-4">
                        <Link to="/" className="hover:text-teal-dark">Home</Link>
                        <Link to="/security" className="hover:text-teal-dark">Security</Link>
                        <Link to="/privacy" className="hover:text-teal-dark">Privacy</Link>
                        <Link to="/terms" className="hover:text-teal-dark">Terms</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
